import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext"; // Import the auth context
import { addOnsAPI } from "../services/api"; // Import the addOnsAPI
import "./MemberAddOns.css"; // Import CSS for styling

const MemberAddOns = ({ bookingId, onAddOnUsed }) => {
  const [addOns, setAddOns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [showGiftForm, setShowGiftForm] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [giftInfo, setGiftInfo] = useState({
    recipientName: "",
    recipientEmail: "",
    recipientPhone: "",
    note: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token } = useAuth(); // Get token from auth context

  // Create a fetchAddOns function that can be called to refresh the add-ons
  const fetchAddOns = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const res = await addOnsAPI.getSummary();
      setAddOns(res.data.data);
    } catch (err) {
      setError(err.userMessage || err.message || "Error loading add-ons");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchAddOns();
  }, [fetchAddOns]);

  // Reset success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const validateGuestForm = () => {
    const errors = {};
    if (!guestInfo.name) errors.name = "Name is required";
    if (!guestInfo.email) errors.email = "Email is required";
    if (!guestInfo.phone) errors.phone = "Phone number is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateGiftForm = () => {
    const errors = {};
    if (!giftInfo.recipientName)
      errors.recipientName = "Recipient name is required";
    if (!giftInfo.recipientEmail)
      errors.recipientEmail = "Recipient email is required";
    if (!giftInfo.recipientPhone)
      errors.recipientPhone = "Recipient phone is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGuestInfoChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleGiftInfoChange = (e) => {
    const { name, value } = e.target;
    setGiftInfo((prev) => ({ ...prev, [name]: value }));
  };

  // Expose the refresh method to parent components
  React.useEffect(() => {
    if (onAddOnUsed) {
      // Register the refresh function with the parent component
      onAddOnUsed("registerRefresh", fetchAddOns);
    }
  }, [onAddOnUsed, fetchAddOns]);

  const useGuestPass = async (e) => {
    e.preventDefault();
    if (!validateGuestForm()) return;

    setIsSubmitting(true);
    try {
      const res = await addOnsAPI.useGuestPass(bookingId, guestInfo);
      setAddOns((prev) => ({
        ...prev,
        guestPasses: res.data.remainingGuestPasses,
      }));
      setSuccessMessage("Guest pass used successfully!");
      setShowGuestForm(false);
      setGuestInfo({ name: "", email: "", phone: "" });
      if (onAddOnUsed) onAddOnUsed("guestPass", res.data.booking);

      // Refresh add-ons after using a guest pass
      fetchAddOns();
    } catch (err) {
      setError(err.userMessage || err.message || "Error using guest pass");
    } finally {
      setIsSubmitting(false);
    }
  };

  const useHalotherapy = async () => {
    if (
      !window.confirm("Are you sure you want to use a halotherapy session?")
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await addOnsAPI.useHalotherapy(bookingId);
      setAddOns((prev) => ({
        ...prev,
        halotherapy: res.data.remainingHalotherapy,
      }));
      setSuccessMessage("Halotherapy added successfully!");
      if (onAddOnUsed) onAddOnUsed("halotherapy", res.data.booking);

      // Refresh add-ons after using halotherapy
      fetchAddOns();
    } catch (err) {
      setError(err.userMessage || err.message || "Error using halotherapy");
    } finally {
      setIsSubmitting(false);
    }
  };

  const giftSession = async (e) => {
    e.preventDefault();
    if (!validateGiftForm()) return;

    setIsSubmitting(true);
    try {
      const res = await addOnsAPI.giftSession(giftInfo);
      setAddOns((prev) => ({
        ...prev,
        giftSessions: res.data.remainingGiftSessions,
      }));
      setSuccessMessage("Session gifted successfully!");
      setShowGiftForm(false);
      setGiftInfo({
        recipientName: "",
        recipientEmail: "",
        recipientPhone: "",
        note: "",
      });

      // Refresh add-ons after gifting a session
      fetchAddOns();
    } catch (err) {
      setError(err.userMessage || err.message || "Error gifting session");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div>Loading add-ons...</div>;
  if (error) return <div className="error-message">{error}</div>;

  const { guestPasses = 0, halotherapy = 0, giftSessions = 0 } = addOns || {};

  const hasAnyAddOns =
    (guestPasses && guestPasses > 0) ||
    (halotherapy && halotherapy > 0) ||
    (giftSessions && giftSessions > 0);

  if (!hasAnyAddOns) {
    return <div className="no-addons">No add-ons available</div>;
  }

  return (
    <div className="member-addons">
      <h3>Your Add-Ons</h3>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="addons-list">
        {guestPasses > 0 && (
          <div className="addon-item">
            <div className="addon-info">
              <strong>Guest Passes:</strong> {guestPasses}
            </div>
            {bookingId && (
              <button
                className="use-addon-btn"
                onClick={() => setShowGuestForm(true)}
                disabled={isSubmitting}
              >
                Add Guest
              </button>
            )}
          </div>
        )}

        {halotherapy > 0 && (
          <div className="addon-item">
            <div className="addon-info">
              <strong>Halotherapy Sessions:</strong> {halotherapy}
            </div>
            {bookingId && (
              <button
                className="use-addon-btn"
                onClick={useHalotherapy}
                disabled={isSubmitting}
              >
                Add Halotherapy
              </button>
            )}
          </div>
        )}

        {giftSessions > 0 && (
          <div className="addon-item">
            <div className="addon-info">
              <strong>Gift Sessions:</strong> {giftSessions}
            </div>
            <button
              className="use-addon-btn gift-btn"
              onClick={() => setShowGiftForm(true)}
              disabled={isSubmitting}
            >
              Send
            </button>
          </div>
        )}
      </div>

      {showGuestForm && (
        <div className="form-overlay">
          <div className="form-container">
            <h4>Add Guest Information</h4>
            <form onSubmit={useGuestPass}>
              <div className="form-group">
                <label htmlFor="name">Guest Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={guestInfo.name}
                  onChange={handleGuestInfoChange}
                  required
                />
                {formErrors.name && (
                  <div className="form-error">{formErrors.name}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Guest Email*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={guestInfo.email}
                  onChange={handleGuestInfoChange}
                  required
                />
                {formErrors.email && (
                  <div className="form-error">{formErrors.email}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Guest Phone*</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={guestInfo.phone}
                  onChange={handleGuestInfoChange}
                  required
                />
                {formErrors.phone && (
                  <div className="form-error">{formErrors.phone}</div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowGuestForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding..." : "Add Guest"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGiftForm && (
        <div className="form-overlay">
          <div className="form-container">
            <h4>Gift a Session</h4>
            <form onSubmit={giftSession}>
              <div className="form-group">
                <label htmlFor="recipientName">Recipient Name*</label>
                <input
                  type="text"
                  id="recipientName"
                  name="recipientName"
                  value={giftInfo.recipientName}
                  onChange={handleGiftInfoChange}
                  required
                />
                {formErrors.recipientName && (
                  <div className="form-error">{formErrors.recipientName}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="recipientEmail">Recipient Email*</label>
                <input
                  type="email"
                  id="recipientEmail"
                  name="recipientEmail"
                  value={giftInfo.recipientEmail}
                  onChange={handleGiftInfoChange}
                  required
                />
                {formErrors.recipientEmail && (
                  <div className="form-error">{formErrors.recipientEmail}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="recipientPhone">Recipient Phone*</label>
                <input
                  type="tel"
                  id="recipientPhone"
                  name="recipientPhone"
                  value={giftInfo.recipientPhone}
                  onChange={handleGiftInfoChange}
                  required
                />
                {formErrors.recipientPhone && (
                  <div className="form-error">{formErrors.recipientPhone}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="note">Personal Note (Optional)</label>
                <textarea
                  id="note"
                  name="note"
                  value={giftInfo.note}
                  onChange={handleGiftInfoChange}
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowGiftForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberAddOns;
